import { useEffect } from 'react'
import { isEmpty, isNonNullish } from 'remeda'
import { useIntersectionObserver } from 'usehooks-ts'
import { useFooter } from '@velasca/hooks/use-footer'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { SansText } from '../SansText/SansText'
import { SansTitle } from '../SansTitle/SansTitle'
import {
  Arrow,
  Background,
  Breadcrumb,
  Breadcrumbs,
  Legal,
  LegalNotes,
  Menu,
  Menus,
  Nav,
  PaymentsImage,
  SecondLevel,
  SecondLevelItem,
  Separator,
} from './Footer.styles'
import type { FooterProps } from './Footer.types'

export const Footer = ({
  title,
  description,
  breadcrumbs,
  payments,
  copyrights,
  blocks,
  legal,
}: FooterProps): JSX.Element => {
  const setIsInView = useFooter((state) => state.setIsInView)
  const { isIntersecting, ref } = useIntersectionObserver()

  useEffect(() => {
    setIsInView(isIntersecting)
  }, [isIntersecting, setIsInView])

  return (
    <Background ref={ref}>
      {!isEmpty(breadcrumbs) && (
        <>
          <Nav aria-label="Breadcrumb">
            <Breadcrumbs>
              {breadcrumbs.map(({ text, link }, i) => {
                return (
                  <Breadcrumb key={text}>
                    <SansText
                      as={link && link !== '#' ? 'a' : 'span'}
                      color="white"
                      {...(link && link !== '#' ? { href: link } : {})}
                    >
                      {text}
                    </SansText>
                    {i < breadcrumbs.length - 1 && <Arrow />}
                  </Breadcrumb>
                )
              })}
            </Breadcrumbs>
          </Nav>

          <Separator />
        </>
      )}

      <h2 className="sr-only">Footer</h2>

      <Menus>
        <Menu>
          <SansTitle
            tracking="normal"
            color="white"
            size="sm"
            as="h3"
            margin="0 0 1.2rem"
            fontWeight="600"
          >
            {title}
          </SansTitle>
          <SansText size="md" color="white" fontWeight="300">
            {description}
          </SansText>
        </Menu>

        {blocks.map((block) => {
          return (
            <Menu key={block.title}>
              <SansTitle
                color="white"
                size="sm"
                margin="0 0 1.2rem"
                as="h3"
                fontWeight="600"
              >
                {block.title}
              </SansTitle>
              <SecondLevel>
                {block.links.map(({ text, link }) => {
                  return (
                    text &&
                    link && (
                      <SecondLevelItem key={link}>
                        <SansText
                          as="a"
                          href={link}
                          color="white"
                          size="md"
                          fontWeight="300"
                        >
                          {text}
                        </SansText>
                      </SecondLevelItem>
                    )
                  )
                })}
              </SecondLevel>
            </Menu>
          )
        })}
      </Menus>

      {isNonNullish(payments) && (
        <PaymentsImage
          lazyload
          lqip
          alt={payments.alt}
          imgSrc={setQueryParams(payments.src, [
            { name: 'width', value: '35' },
          ])}
          sources={buildIntrinsicWidthSources({
            src: payments.src,
            maxWidth: payments.width,
          })}
        />
      )}

      <Separator />
      <LegalNotes>
        <SansText
          as="div"
          color="gray"
          margin="2rem 0 0 0"
          dangerouslySetInnerHTML={{ __html: copyrights }}
        />
        <Legal>
          {legal.map(({ text, link }) => {
            return (
              text &&
              link && (
                <li key={link}>
                  <SansText as="a" color="gray" href={link}>
                    {text}
                  </SansText>
                </li>
              )
            )
          })}
        </Legal>
      </LegalNotes>
    </Background>
  )
}
